import { ref } from "vue";

/**
 * Filtre multi-sélection des workout-types du récap « Mes séances », partagé entre les vues
 * week / month / year / total. Source de vérité = param URL `?wt=alias1,alias2` (cohérent avec le
 * `?d=` du planning) : survit au refresh, partageable, et conservé au switch de vue puisque chaque
 * page réinjecte `window.location.search` dans sa navigation.
 *
 * Le ref est module-scope (singleton) → cliquer un type dans le récap met à jour la timeline et les
 * stats qui consomment le même composable, sans store intermédiaire.
 */

const readFromUrl = (): string[] =>
{
	const wt = new URLSearchParams(window.location.search).get("wt");

	return wt ? wt.split(",").filter(Boolean) : [];
};

const selectedAliases = ref<string[]>(readFromUrl());

const writeToUrl = (aliases: string[]): void =>
{
	const url = new URL(window.location.href);

	if (aliases.length > 0)
	{
		url.searchParams.set("wt", aliases.join(","));
	}
	else
	{
		url.searchParams.delete("wt");
	}

	history.replaceState(history.state, "", url.toString());
};

export default function useWorkoutTypesFilter()
{
	const toggle = (alias: string): void =>
	{
		selectedAliases.value = selectedAliases.value.includes(alias)
			? selectedAliases.value.filter(a => a !== alias)
			: [ ...selectedAliases.value, alias ];

		writeToUrl(selectedAliases.value);
	};

	const isSelected = (alias: string): boolean => selectedAliases.value.includes(alias);

	/** True si le type passe le filtre : aucun filtre actif = tout passe. */
	const matches = (alias: string | null | undefined): boolean =>
		selectedAliases.value.length === 0 || (!! alias && selectedAliases.value.includes(alias));

	const clear = (): void =>
	{
		selectedAliases.value = [];
		writeToUrl([]);
	};

	return { selectedAliases, toggle, isSelected, matches, clear };
}
